import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignaturePadField from "../../components/SignaturePadField";
import {
  Loader2, AlertCircle, CheckCircle2, FileText, Download,
  MapPin, Calendar, PenLine, Shield,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_LOADING = "loading";
const STEP_WELCOME = "welcome";
const STEP_VIEW = "view";
const STEP_SIGN = "sign";
const STEP_SUCCESS = "success";
const STEP_EXPIRED = "expired";
const STEP_ERROR = "error";

function formatApiDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d).join(", ");
  return fallback;
}

function SecurityFooter({ className = "" }) {
  return (
    <p className={`text-xs text-slate-400 flex items-center gap-1.5 ${className}`}>
      <Shield className="w-3.5 h-3.5 shrink-0" />
      Connessione sicura. I dati sono trattati nel rispetto della privacy.
    </p>
  );
}

export default function DelegaFirmaPortal() {
  const { token } = useParams();
  const [step, setStep] = useState(STEP_LOADING);
  const [data, setData] = useState(null);
  const [delegaLetta, setDelegaLetta] = useState(false);
  const [luogo, setLuogo] = useState("");
  const [signing, setSigning] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const sigPadRef = useRef(null);
  const pdfBlobRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/deblistering/public/delega-link/${token}`);
        setData(res.data);
        setDelegaLetta(!!res.data.checklist?.delega_vista);
        if (res.data.checklist?.delega_firmata) {
          setStep(STEP_SUCCESS);
        } else if (res.data.checklist?.delega_vista) {
          setStep(STEP_SIGN);
        } else {
          setStep(STEP_WELCOME);
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 410 || status === 404 || status === 409) {
          setStep(status === 409 ? STEP_SUCCESS : STEP_EXPIRED);
        } else {
          setStep(STEP_ERROR);
        }
      }
    })();
  }, [token]);

  useEffect(() => {
    if (step !== STEP_VIEW && step !== STEP_SIGN) return undefined;
    let cancelled = false;
    (async () => {
      setPdfLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/deblistering/public/delega-link/${token}/anteprima`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        if (pdfBlobRef.current) URL.revokeObjectURL(pdfBlobRef.current);
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        pdfBlobRef.current = blobUrl;
        setPdfUrl(blobUrl);
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiDetail(err.response?.data?.detail, "Impossibile caricare l'anteprima delega"));
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (pdfBlobRef.current) {
        URL.revokeObjectURL(pdfBlobRef.current);
        pdfBlobRef.current = null;
      }
    };
  }, [step, token]);

  const handleDelegaVista = async () => {
    if (!delegaLetta) {
      toast.error("Conferma di aver letto la delega");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/deblistering/public/delega-link/${token}/delega/visto`);
      setStep(STEP_SIGN);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore"));
    }
  };

  const handleSign = async () => {
    if (!luogo.trim()) {
      toast.error("Inserisci il luogo della firma");
      return;
    }
    if (sigPadRef.current?.isEmpty()) {
      toast.error("Disegna la firma prima di procedere");
      return;
    }
    setSigning(true);
    try {
      const firma = sigPadRef.current.toDataURL("image/png");
      await axios.post(`${API_URL}/api/deblistering/public/delega-link/${token}/firma`, {
        firma_familiare: firma,
        luogo: luogo.trim(),
      });
      toast.success("Delega firmata con successo!");
      setStep(STEP_SUCCESS);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore nella firma"));
    } finally {
      setSigning(false);
    }
  };

  if (step === STEP_LOADING) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (step === STEP_EXPIRED) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link non valido</h1>
          <p className="text-slate-600 text-sm">Il link è scaduto o non è più utilizzabile. Contatta la struttura per riceverne uno nuovo.</p>
        </div>
      </div>
    );
  }

  if (step === STEP_ERROR) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Errore</h1>
          <p className="text-slate-600 text-sm">Impossibile caricare la pagina. Riprova più tardi o contatta la struttura.</p>
        </div>
      </div>
    );
  }

  if (step === STEP_SUCCESS) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Delega firmata</h1>
          <p className="text-slate-600 text-sm">
            Grazie! La delega per il servizio deblistering
            {data?.ospite_nome ? ` di ${data.ospite_nome}` : ""} è stata firmata e archiviata in modo sicuro.
          </p>
        </div>
      </div>
    );
  }

  const oggi = new Date().toLocaleDateString("it-IT");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-cyan-700 text-white px-4 py-4 sm:py-5">
        <div className="max-w-2xl mx-auto">
          <p className="text-cyan-100 text-xs sm:text-sm">{data?.struttura_nome || "Struttura"}</p>
          <h1 className="text-lg sm:text-xl font-bold mt-0.5">Firma delega deblistering</h1>
          {data?.ospite_nome && (
            <p className="text-cyan-50 text-sm mt-1">Ospite: {data.ospite_nome}</p>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        {data?.messaggio && step === STEP_WELCOME && (
          <div className="mb-4 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900">
            {data.messaggio}
          </div>
        )}

        {data?.missing_fields?.length > 0 && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p className="font-semibold mb-1">Dati mancanti nel fascicolo</p>
            <p className="text-red-700">Contatta la struttura: alcuni dati obbligatori non sono completi e la firma non è al momento possibile.</p>
          </div>
        )}

        {step === STEP_WELCOME && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <FileText className="w-10 h-10 text-cyan-600 mb-4" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Delega per il servizio farmacia</h2>
            <p className="text-sm text-slate-600 mb-6">
              Le verrà mostrata la delega da firmare digitalmente per autorizzare il servizio di preparazione farmaci in blister.
            </p>
            <button
              type="button"
              onClick={() => setStep(STEP_VIEW)}
              disabled={!data?.can_sign}
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-cyan-600 text-white font-medium hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Procedi
            </button>
            <SecurityFooter className="mt-6" />
          </div>
        )}

        {step === STEP_VIEW && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900">Visiona delega</h2>
            </div>
            <div className="px-3 sm:px-4 pb-2">
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-100">
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-800 text-white text-xs sm:text-sm">
                  <span className="font-medium truncate">Delega deblistering</span>
                  {pdfUrl && (
                    <a href={pdfUrl} download="delega_farmacia.pdf" className="p-1.5 rounded hover:bg-white/10" title="Scarica PDF">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <div className="bg-white" style={{ minHeight: 320 }}>
                  {pdfLoading ? (
                    <div className="flex items-center justify-center h-[50vh] max-h-[520px] min-h-[280px]">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                    </div>
                  ) : pdfUrl ? (
                    <object data={pdfUrl} type="application/pdf" className="w-full h-[50vh] max-h-[520px] min-h-[280px] bg-white" aria-label="Delega deblistering">
                      <iframe title="Delega" src={pdfUrl} className="w-full h-[50vh] max-h-[520px] min-h-[280px]" />
                    </object>
                  ) : (
                    <div className="flex items-center justify-center h-[280px] text-sm text-slate-500 px-4 text-center">
                      Anteprima non disponibile. Ricarica la pagina o contatta la struttura.
                    </div>
                  )}
                </div>
              </div>

              <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
                <Checkbox
                  checked={delegaLetta}
                  onCheckedChange={(v) => setDelegaLetta(!!v)}
                  className="mt-0.5 border-slate-400 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                />
                <span className="text-sm sm:text-base text-slate-700">Ho letto la delega</span>
              </label>

              <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6 pb-4">
                <button
                  type="button"
                  onClick={() => setStep(STEP_WELCOME)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
                >
                  Indietro
                </button>
                <button
                  type="button"
                  onClick={handleDelegaVista}
                  disabled={!delegaLetta}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
                >
                  Continua alla firma
                </button>
              </div>
              <SecurityFooter className="pb-2" />
            </div>
          </div>
        )}

        {step === STEP_SIGN && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Firma delega</h2>
            <p className="text-sm text-slate-600 mb-5">Firma qui sotto con il dito, mouse o stylus.</p>

            <div className="mb-4 max-w-xs">
              <Label className="text-slate-600 text-sm">Luogo</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                <Input
                  value={luogo}
                  onChange={(e) => setLuogo(e.target.value)}
                  placeholder="Es. Roma"
                  className="h-11"
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4">
              <Calendar className="w-4 h-4" />
              <span>Data: {oggi}</span>
            </div>

            <SignaturePadField
              ref={sigPadRef}
              backgroundColor="#ffffff"
              canvasClassName="w-full h-44 sm:h-52 touch-none"
              clearVariant="link"
              clearClassName="text-sm text-slate-500 flex items-center gap-1.5 hover:text-cyan-700 transition-colors"
              actionsClassName="flex flex-wrap items-center justify-between gap-2 mt-3"
            />

            <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-slate-50 text-xs text-slate-600">
              <PenLine className="w-4 h-4 shrink-0 mt-0.5" />
              <span>La delega sarà firmata digitalmente e archiviata in modo sicuro.</span>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
              <button
                type="button"
                onClick={() => setStep(STEP_VIEW)}
                disabled={signing}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
              >
                Indietro
              </button>
              <button
                type="button"
                onClick={handleSign}
                disabled={signing}
                className="flex-1 px-4 py-2.5 rounded-xl bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {signing ? "Firma in corso..." : "Conferma firma"}
              </button>
            </div>
            <SecurityFooter className="mt-4" />
          </div>
        )}
      </main>
    </div>
  );
}
